import * as React from "react";

import TextField from "@mui/material/TextField";
import throttle from "lodash/throttle";
import {
  ILookupItemSimpleModel,
  ReferrerTypeEnumModel,
} from "../../types/types";
import {
  searchContacts,
  searchReferrers,
} from "../../store/modules/common/commonActions";
import Autocomplete from "@mui/material/Autocomplete";
import { useTheme } from "@mui/material/styles";
import CircularProgress from "@mui/material/CircularProgress";

export interface IProps {
  onChange?: (
    event: React.ChangeEvent<{}>,
    value: ILookupItemSimpleModel[],
  ) => void;
  helperText: any;
  error: any;
  limit?: number;
  id: string;
  label: string;
  selected: ILookupItemSimpleModel[];
  referrerType: ReferrerTypeEnumModel;
  /** When false, only existing contacts can be picked (no "Add ..." option). */
  allowAddNew?: boolean;
  placeholder?: string;
}
export default function ReferrarAutoComplete(props: IProps) {
  // The live theme, so the "add new" hint follows affiliate branding.
  const brand = useTheme();

  const {
    onChange,
    helperText,
    error,
    id,
    label,
    selected,
    referrerType,
    allowAddNew = true,
    placeholder = "Search or add new Referrer",
  } = props;
  const [inputValue, setInputValue] = React.useState("");
  const [options, setOptions] = React.useState<
    readonly ILookupItemSimpleModel[]
  >([]);
  const [isLoading, setIsLoading] = React.useState(false);

  const fetch = React.useMemo(
    () =>
      throttle(async (value: string) => {
        setIsLoading(true);
        try {
          const response = await searchContacts(value);
          setOptions(response ?? []);
        } finally {
          setIsLoading(false);
        }
      }, 300),
    [],
  );

  React.useEffect(() => {
    //trigger search after 1 second when user stops typing
    if (inputValue.length >= 2 && inputValue.length <= 10) {
      const search = setTimeout(() => {
        fetch(inputValue);
      }, 1000);

      return () => clearTimeout(search);
    } else if (inputValue.length === 0) {
      // Clear options when input is empty
      setOptions([]);
    }
  }, [inputValue, fetch]);

  // Create options with "Add [name]" option if search term doesn't match existing options
  const getOptionsWithAddNew = React.useMemo(() => {
    if (!allowAddNew || !inputValue.trim() || inputValue.length < 2) {
      return options;
    }

    const searchTerm = inputValue.trim().toLowerCase();
    const hasExactMatch = options.some(
      (option) => option.name?.toLowerCase() === searchTerm,
    );

    if (!hasExactMatch && inputValue.trim().length > 0) {
      // Add "Add [name]" option at the beginning
      const addNewOption: ILookupItemSimpleModel & { isAddNew?: boolean } = {
        id: -1, // Special ID to identify this as "add new" option
        name: `Add "${inputValue.trim()}"`,
        isAddNew: true, // Custom property to identify this option
      };
      return [addNewOption, ...options];
    }

    return options;
  }, [options, inputValue, allowAddNew]);

  return (
    <Autocomplete
      multiple
      freeSolo={allowAddNew}
      id={id}
      getOptionLabel={(option) =>
        typeof option === "string" ? option : `${option.name}`
      }
      // isOptionEqualToValue={(option, value) => option.id === value.id}
      getOptionKey={(option) => {
        if (typeof option === "string") {
          return option;
        }
        if (option.id) {
          return option.id;
        }
        return option.id!;
      }}
      filterOptions={(x) => x}
      options={getOptionsWithAddNew}
      autoComplete
      includeInputInList
      fullWidth={true}
      filterSelectedOptions
      value={selected ?? []}
      getOptionDisabled={(option) =>
        props.limit && selected.length >= props.limit ? true : false
      }
      onChange={(event: any, newValue: (ILookupItemSimpleModel | string)[]) => {
        const processedValues = newValue.map((item) => {
          if (!allowAddNew) {
            return item as ILookupItemSimpleModel;
          } else if (typeof item === "string") {
            return { id: Math.floor(Math.random() * -1e12), name: item };
          } else if ((item as any).isAddNew) {
            // Handle "Add new" option - extract the actual name from "Add [name]"
            const actualName = inputValue.trim();
            setInputValue(""); // Clear the input after adding
            return { id: Math.floor(Math.random() * -1e12), name: actualName };
          } else {
            return item;
          }
        });
        onChange!(event, processedValues);
      }}
      onInputChange={(event, newInputValue) => {
        setInputValue(newInputValue);
      }}
      renderInput={(params) => (
        <TextField
          variant="outlined"
          {...params}
          placeholder={placeholder}
          label={label}
          fullWidth
          helperText={helperText}
          error={error}
          InputLabelProps={{
            shrink: true,
            sx: {
              // position: "relative",
              // transform: "none",
              // marginBottom: "8px",
            },
          }}
          inputProps={{
            ...params.inputProps,
            maxLength: 50,
            style: {
              // paddingTop: 0,
            },
          }}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {isLoading && <CircularProgress color="primary" size={20} />}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
      renderOption={(props, option) => {
        const isAddNew = (option as any).isAddNew;
        return (
          <li
            {...props}
            style={{
              ...props.style,
              fontWeight: isAddNew ? "bold" : "normal",
              color: isAddNew ? brand.palette.primary.dark : "inherit",
              fontStyle: isAddNew ? "italic" : "normal",
            }}
          >
            {typeof option === "string" ? option : option.name}
          </li>
        );
      }}
    />
  );
}
